
import { useState, useEffect } from "react";
import { ProjectTask, ProjectTemplate } from "../types";
import { calculateEndDate } from "../utils/dateUtils";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export function useTemplates() {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [selectedTemplateTasks, setSelectedTemplateTasks] = useState<ProjectTask[]>([]);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isCustomProjectDialogOpen, setIsCustomProjectDialogOpen] = useState(false);
  const [isImportAlertOpen, setIsImportAlertOpen] = useState(false);
  
  // Template form states
  const [templateProjectName, setTemplateProjectName] = useState("");
  const [templateProjectDescription, setTemplateProjectDescription] = useState("");
  const [templateProjectLead, setTemplateProjectLead] = useState("");
  const [templateStartDate, setTemplateStartDate] = useState("");
  const [templateEndDate, setTemplateEndDate] = useState("");
  const [excludeWeekends, setExcludeWeekends] = useState(true);

  // Fetch templates from database with proper loading state
  const { data: dbTemplates = [], isLoading: templatesLoading, error: templatesError } = useQuery({
    queryKey: ['project-templates'],
    queryFn: async () => {
      console.log('Fetching templates from database...');
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching templates:', error);
        throw error;
      }
      console.log('Templates fetched successfully:', data);
      return data || [];
    }
  });

  // Convert database templates to the expected format with proper error handling
  const convertedTemplates: ProjectTemplate[] = dbTemplates.map(template => {
    let parsedTasks: ProjectTask[] = [];
    if (template.tasks) {
      try {
        parsedTasks = Array.isArray(template.tasks) ? 
          template.tasks as unknown as ProjectTask[] : 
          JSON.parse(JSON.stringify(template.tasks)) as ProjectTask[];
      } catch (error) {
        console.error('Error parsing template tasks:', error);
        parsedTasks = [];
      }
    }

    const totalDuration = parsedTasks.reduce((sum, task) => sum + task.duration, 0);

    return {
      id: template.id,
      title: template.title,
      tasks: parsedTasks,
      milestones: template.milestones,
      totalDuration: totalDuration,
      description: template.description
    };
  });

  const handleUseTemplate = (templateName: string, templateId: number) => {
    console.log('handleUseTemplate called with:', templateName, templateId);
    
    // Find template in converted templates
    const template = convertedTemplates.find(t => t.id === templateId);
    if (!template) {
      console.error('Template not found:', templateId);
      return;
    }
    
    console.log('Template found:', template);
    
    // Set all required state
    setSelectedTemplate(templateName);
    setSelectedTemplateTasks([...template.tasks]);
    setTemplateProjectName("");
    setTemplateProjectDescription("");
    setTemplateProjectLead("");
    setTemplateStartDate("");
    setTemplateEndDate("");
    
    // Open dialog immediately - no setTimeout needed
    console.log('Opening template dialog');
    setIsTemplateDialogOpen(true);
  };

  const handleTaskDurationChange = (index: number, increase: boolean) => {
    setSelectedTemplateTasks(prev => {
      const updatedTasks = [...prev];
      if (increase) {
        updatedTasks[index].duration += 1;
      } else if (updatedTasks[index].duration > 1) {
        updatedTasks[index].duration -= 1;
      }
      return updatedTasks;
    });
  };

  const handleTemplateStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setTemplateStartDate(newStartDate);
    
    // Auto-calculate end date when start date changes
    if (newStartDate && selectedTemplateTasks.length > 0) {
      const totalDuration = selectedTemplateTasks.reduce((sum, task) => sum + task.duration, 0);
      const calculatedEndDate = calculateEndDate(newStartDate, totalDuration, excludeWeekends);
      setTemplateEndDate(calculatedEndDate);
    } else {
      setTemplateEndDate("");
    }
  };

  const handleExcludeWeekendsChange = (checked: boolean) => {
    setExcludeWeekends(checked);
    
    // Auto-calculate end date when weekend setting changes
    if (templateStartDate && selectedTemplateTasks.length > 0) {
      const totalDuration = selectedTemplateTasks.reduce((sum, task) => sum + task.duration, 0);
      const calculatedEndDate = calculateEndDate(templateStartDate, totalDuration, checked);
      setTemplateEndDate(calculatedEndDate);
    }
  };

  return {
    // Template data
    templates: convertedTemplates,
    templatesLoading,
    templatesError,
    
    // Selected template state
    selectedTemplate,
    setSelectedTemplate,
    selectedTemplateTasks,
    setSelectedTemplateTasks,
    
    // Dialog states
    isTemplateDialogOpen,
    setIsTemplateDialogOpen,
    isCustomProjectDialogOpen,
    setIsCustomProjectDialogOpen,
    isImportAlertOpen,
    setIsImportAlertOpen,
    
    // Form states
    templateProjectName,
    setTemplateProjectName,
    templateProjectDescription,
    setTemplateProjectDescription,
    templateProjectLead,
    setTemplateProjectLead,
    templateStartDate,
    setTemplateStartDate,
    templateEndDate,
    setTemplateEndDate,
    excludeWeekends,
    setExcludeWeekends,
    
    // Handlers
    handleUseTemplate,
    handleTaskDurationChange,
    handleTemplateStartDateChange,
    handleExcludeWeekendsChange,
  };
}
